import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  InfluencerPublicActiveInfluencersResponse,
  InfluencerPublicBannersResponse,
  InfluencerPublicCategory,
  InfluencerPublicCategoryVideosParams,
  InfluencerPublicCategoryVideosResponse,
  InfluencerPublicDetailedListParams,
  InfluencerPublicDetailedListResponse,
  InfluencerPublicDetailResponse,
  InfluencerPublicFilterOptionsResponse,
} from '../../../models';
import { HttpService } from '../http.service';

@Injectable({
  providedIn: 'root'
})
export class InfluencerService extends HttpService<any, any> {
  constructor(
    httpClient: HttpClient,
    @Inject('API_URL') apiUrl: string,
    @Inject('API_VERSION') apiVersion: string
  ) {
    super(httpClient, apiUrl, apiVersion, 'influencer');
  }

  getInfluencers(page = 1, limit = 10, search = ''): Observable<any> {
    const suffix = search ? `search=${encodeURIComponent(search)}` : '';
    return this.getAllPaged('admin', page, suffix, limit) as Observable<any>;
  }

  getPublicInfluencerBanners(): Observable<InfluencerPublicBannersResponse> {
    return this.getAll('public/banners') as Observable<InfluencerPublicBannersResponse>;
  }

  getActivePublicInfluencers(): Observable<InfluencerPublicActiveInfluencersResponse> {
    return this.getAll('public/active-influencers') as Observable<InfluencerPublicActiveInfluencersResponse>;
  }

  getPublicInfluencersDetailedList(params: InfluencerPublicDetailedListParams = {}): Observable<InfluencerPublicDetailedListResponse> {
    return this.getBy('public/influencers-detailed-list', params) as Observable<InfluencerPublicDetailedListResponse>;
  }

  getPublicInfluencerFilterOptions(): Observable<InfluencerPublicFilterOptionsResponse> {
    return this.getAll('public/filter-options') as Observable<InfluencerPublicFilterOptionsResponse>;
  }

  getPublicInfluencerByIdOrSlug(idOrSlug: number | string): Observable<InfluencerPublicDetailResponse> {
    return this.getAll(`public/${encodeURIComponent(String(idOrSlug))}`) as Observable<InfluencerPublicDetailResponse>;
  }

  getPublicInfluencerCategories(idOrSlug: number | string): Observable<InfluencerPublicCategory[]> {
    return this.getAll(`public/${encodeURIComponent(String(idOrSlug))}/categories`) as Observable<InfluencerPublicCategory[]>;
  }

  getPublicInfluencerCategoryVideos(
    idOrSlug: number | string,
    categoryIdOrSlug: number | string,
    params: InfluencerPublicCategoryVideosParams = {}
  ): Observable<InfluencerPublicCategoryVideosResponse> {
    const encodedInfluencerIdentifier = encodeURIComponent(String(idOrSlug));
    const encodedCategoryIdentifier = encodeURIComponent(String(categoryIdOrSlug));
    return this.getBy(
      `public/${encodedInfluencerIdentifier}/categories/${encodedCategoryIdentifier}/videos`,
      params
    ) as Observable<InfluencerPublicCategoryVideosResponse>;
  }

  filterInfluencers(payload: any): Observable<any> {
    return this.filterAllActionPagedAdmin(payload, 'admin') as Observable<any>;
  }

  getStatusCounts(): Observable<any> {
    return this.getAll('status-counts');
  }

  getInfluencerById(id: number): Observable<any> {
    return this.getById(id, 'admin') as Observable<any>;
  }

  createInfluencer(payload: any): Observable<any> {
    return this.create(payload);
  }

  updateInfluencer(id: number, payload: any): Observable<any> {
    return this.update(payload, '', id);
  }

  updateInfluencerStatus(id: number, payload: { is_active: boolean }): Observable<any> {
    return this.patch(id, payload);
  }

  updateInfluencerOrder(payload: { influencers: Array<{ id: number; position: number }> }): Observable<unknown> {
    return this.update(payload, 'positions/align');
  }

  deleteInfluencer(id: number): Observable<any> {
    return this.delete(id);
  }
}
